<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  name: 'UserInfo',
});
</script>
<script setup lang="ts">
import { ref, reactive, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { FormInstance, FormRules } from 'element-plus';
import type {
  UserInfoProps,
  UserDetailInfo,
  ChangePasswordFormData,
  ChangePasswordParams,
  TabIndex,
} from './types';

;

const props = withDefaults(defineProps<UserInfoProps>(), {
  loading: false,
  logoSrc: '',
  logoTitleSrc: '',
  avatarSrc: '',
  backIconSrc: '',
  forwardIconSrc: '',
  passwordVisibleIconSrc: '',
  passwordHiddenIconSrc: '',
  showBackButton: true,
  passwordRegex: () =>
    /^(?=.*[a-zA-Z])(?=.*\d|.*[!#$%&*()]).{8,16}$|^(?=.*\d)(?=.*[!#$%&*()]).{8,16}$/,
  dialogWidth: '26%',
  timeFormatter: (time: string) => time,
});

const emit = defineEmits<{
  'update:loading': [value: boolean];
  'change-password': [data: ChangePasswordParams];
  back: [];
}>();

const { t } = useI18n();
const router = useRouter();

const dialogVisible = ref(false);
const formRefEdit = ref<FormInstance>();
const curIndex = ref<TabIndex>(1);
const showPassword1 = ref(false);
const showPassword2 = ref(false);
const showPassword3 = ref(false);

// 用户信息和详细信息
const userInfo = defineModel<any>('userInfo', { required: true });
const detailInfo = defineModel<UserDetailInfo>('detailInfo', { required: true });

// 编辑表单
const defaultForm: ChangePasswordFormData = {
  newPassword: '',
  secondPassword: '',
  oldPassword: '',
};
const editForm = reactive<ChangePasswordFormData>({ ...defaultForm });

// 计算角色列表
const roleList = computed(() => {
  if (!detailInfo.value) return [];

  // 支持两种数据结构
  if (detailInfo.value.klondUserRoles) {
    return detailInfo.value.klondUserRoles.map((item) => item.roleName);
  }

  if (detailInfo.value.roleNames) {
    return detailInfo.value.roleNames;
  }

  return [];
});

// 计算显示名称
const displayName = computed(() => {
  if (!detailInfo.value) return '-';
  return detailInfo.value.name || detailInfo.value.nickName || '-';
});

// 密码验证
const validatePass = (rule: any, value: any, callback: any) => {
  if (value === '') {
    callback(new Error(t('请输入')));
  } else {
    if (!editForm) return;
    if (!props.passwordRegex.test(editForm.newPassword)) {
      callback(new Error(t('格式错误')));
    }
    if (editForm.secondPassword !== '') {
      formRefEdit.value?.validateField('secondPassword', () => null);
    }
    callback();
  }
};

const validatePasswordSec = (rule: any, value: string, callback: any) => {
  if (value === '') {
    callback(new Error(t('请输入')));
  } else if (value !== editForm.newPassword) {
    callback(new Error(t('两次输入不一致')));
  } else {
    callback();
  }
};

// 校验规则
const rulesEdit = reactive<FormRules>({
  oldPassword: [
    { required: true, message: t('请输入'), trigger: 'blur' },
    { min: 1, max: 16, message: t('最长16位'), trigger: 'blur' },
  ],
  newPassword: [{ required: true, validator: validatePass, trigger: 'blur' }],
  secondPassword: [{ required: true, validator: validatePasswordSec, trigger: 'blur' }],
});

// 修改密码
const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => {
    if (valid) {
      emit('update:loading', true);
      emit('change-password', {
        oldPassword: editForm.oldPassword,
        newPassword: editForm.newPassword,
        userId: detailInfo.value?.id,
        userName: detailInfo.value?.userName,
      });
    }
  });
};

// 打开弹窗
const openEdit = () => {
  showPassword1.value = showPassword2.value = showPassword3.value = false;
  emit('update:loading', false);
  Object.assign(editForm, { ...defaultForm });
  setTimeout(() => {
    nextTick(() => {
      formRefEdit.value?.clearValidate();
    });
  }, 0);
  dialogVisible.value = true;
};

// 关闭弹窗
const closeDialog = () => {
  dialogVisible.value = false;
};

// 返回
const handleBack = () => {
  emit('back');
  router.back();
};

// 脱敏邮箱
const anonymizeEmail = (email?: string) => {
  if (!email) return '-';
  const [name, domain] = email.split('@');
  const len = name.length;
  if (len <= 1) return email;
  const start = len >= 8 ? name.slice(0, 4) : len >= 6 ? name.slice(0, 3) : name[0];
  return `${start}${'*'.repeat(len - start.length - 1)}${name.slice(-1)}@${domain}`;
};

// 暴露方法
defineExpose({
  closeDialog,
});
</script>

<template>
  <div>
    <div />

    <!-- Logo 区域 -->
    <div class="sidebar-logo-container">
      <slot name="logo">
        <img v-if="logoSrc" class="logo-img" :src="logoSrc" alt="logo" />
        <img v-if="logoTitleSrc" class="sidebar-title" :src="logoTitleSrc" alt="logo-title" />
      </slot>
    </div>

    <!-- 导航栏 -->
    <slot name="navbar" />

    <!-- 主内容 -->
    <div class="main-content">
      <div class="user-info mx-auto relative">
        <div class="ui-contain rounded-xl overflow-hidden flex">
          <!-- 左侧用户信息 -->
          <div class="ui-left bg-white">
            <img :src="avatarSrc" alt="avatar" />
            <p class="text-center mb-[10px] text-[17px] text-black">
              {{ userInfo?.username || userInfo?.userName || $t('未知用户') }}
            </p>

            <!-- 角色列表 -->
            <div class="role-list mb-[42px]">
              <slot name="roles" :roles="roleList">
                <div v-for="(role, index) in roleList" :key="index" class="role-item">
                  {{ role }}
                </div>
              </slot>
            </div>

            <!-- 标签页切换 -->
            <div class="uil-list">
              <div
                :class="curIndex === 1 ? 'uil-item uil-active' : 'uil-item'"
                @click="curIndex = 1"
              >
                {{ $t('账户信息') }}
              </div>
              <div
                :class="curIndex === 2 ? 'uil-item uil-active' : 'uil-item'"
                @click="curIndex = 2"
              >
                {{ $t('账号与安全') }}
              </div>
            </div>
          </div>

          <!-- 右侧详细信息 -->
          <div class="ui-right">
            <div class="uir-title">
              {{ curIndex === 1 ? $t('基本信息') : $t('安全设置') }}
            </div>

            <!-- 基本信息 / 安全设置 -->
            <div class="uir-basic bg-white rounded-xl overflow-hidden">
              <!-- 账户信息 -->
              <div v-show="curIndex === 1" class="uirb-item mb-7">
                <div>{{ $t('用户名') }}</div>
                <div class="uirb-text">
                  {{ detailInfo?.userName }}
                </div>
              </div>
              <div v-show="curIndex === 1" class="uirb-item">
                <div>{{ $t('姓名') }}</div>
                <div class="uirb-text">
                  {{ displayName }}
                </div>
              </div>

              <!-- 安全设置 -->
              <div v-show="curIndex === 2" class="uirb-item mb-7">
                <div>
                  <div>{{ $t('邮箱') }}</div>
                  <div class="uirb-tips">
                    {{ $t('用于身份识别') }}
                  </div>
                </div>
                <div class="uirb-text">
                  {{ anonymizeEmail(detailInfo?.email) }}
                </div>
              </div>
              <div v-show="curIndex === 2" class="uirb-item cursor-pointer" @click="openEdit">
                <div>
                  <div>{{ $t('修改登录密码') }}</div>
                  <div class="uirb-tips">
                    {{ $t('建议您定期更改密码以保证账户安全') }}
                  </div>
                </div>
                <img v-if="forwardIconSrc" :src="forwardIconSrc" class="forward" alt="forward" />
              </div>
            </div>

            <!-- 登录信息 -->
            <div v-show="curIndex === 1" class="uir-title mt-8!">
              {{ $t('登录信息') }}
            </div>
            <div v-show="curIndex === 1" class="uir-basic bg-white rounded-xl overflow-hidden">
              <div class="uirb-item mb-7">
                <div>{{ $t('上次登录IP') }}</div>
                <div class="uirb-text">
                  {{ detailInfo?.lastLoginIp || '-' }}
                </div>
              </div>
              <div class="uirb-item">
                <div>{{ $t('上次登录时间') }}</div>
                <div class="uirb-text">
                  {{ detailInfo?.lastLoginTime ? timeFormatter(detailInfo.lastLoginTime) : '-' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 返回按钮 -->
        <img
          v-if="showBackButton && backIconSrc"
          :src="backIconSrc"
          class="back cursor-pointer"
          alt="back"
          @click="handleBack"
        />
      </div>
    </div>

    <!-- 修改密码对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :width="dialogWidth"
      :close-on-click-modal="false"
      custom-class="common-dialog borderRa12"
    >
      <div class="common-dialog-contain24">
        <div class="cdc-top">
          <div class="cdct-title cdct-700">
            <p>{{ $t('修改登录密码') }}</p>
          </div>
        </div>

        <el-form
          ref="formRefEdit"
          :model="editForm"
          :rules="rulesEdit"
          label-position="top"
          class="common-dialog-form-edit"
        >
          <!-- 旧密码 -->
          <el-form-item class="cdfe-item" prop="oldPassword" :label="$t('旧密码')">
            <el-input
              v-model="editForm.oldPassword"
              :placeholder="$t('请输入')"
              :type="showPassword1 ? 'text' : 'password'"
            >
              <template #suffix>
                <img
                  v-if="passwordVisibleIconSrc && passwordHiddenIconSrc"
                  class="input-pas"
                  :src="showPassword1 ? passwordVisibleIconSrc : passwordHiddenIconSrc"
                  alt="toggle"
                  @click="showPassword1 = !showPassword1"
                />
              </template>
            </el-input>
          </el-form-item>

          <!-- 新密码 -->
          <el-form-item class="cdfe-item" prop="newPassword" :label="$t('新密码')">
            <el-input
              v-model="editForm.newPassword"
              :placeholder="$t('请输入')"
              :type="showPassword2 ? 'text' : 'password'"
            >
              <template #suffix>
                <img
                  v-if="passwordVisibleIconSrc && passwordHiddenIconSrc"
                  class="input-pas"
                  :src="showPassword2 ? passwordVisibleIconSrc : passwordHiddenIconSrc"
                  alt="toggle"
                  @click="showPassword2 = !showPassword2"
                />
              </template>
            </el-input>
          </el-form-item>

          <!-- 确认新密码 -->
          <el-form-item class="cdfe-item" prop="secondPassword" :label="$t('确认新密码')">
            <el-input
              v-model="editForm.secondPassword"
              :placeholder="$t('请输入')"
              :type="showPassword3 ? 'text' : 'password'"
            >
              <template #suffix>
                <img
                  v-if="passwordVisibleIconSrc && passwordHiddenIconSrc"
                  class="input-pas"
                  :src="showPassword3 ? passwordVisibleIconSrc : passwordHiddenIconSrc"
                  alt="toggle"
                  @click="showPassword3 = !showPassword3"
                />
              </template>
            </el-input>
          </el-form-item>
        </el-form>

        <!-- 密码规则提示 -->
        <div class="update-tip">
          <slot name="passwordTips">
            <p>{{ $t('common.passwordRule1') }}</p>
            <p>{{ $t('common.passwordRule2') }}</p>
          </slot>
        </div>
      </div>

      <!-- 对话框底部按钮 -->
      <div class="common-dialog-footer" style="margin-top: 20px">
        <el-button class="cdf-btn" @click="closeDialog">
          {{ $t('关闭') }}
        </el-button>
        <el-button :loading="loading" class="cdf-btn cdf-sure" @click="submitForm(formRefEdit)">
          {{ $t('保存') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.main-content {
  display: flex;
  justify-content: center;
  min-height: calc(100vh - 48px);
  padding: 18px 0 34px;
  background: linear-gradient(180deg, #134aa4 0%, #477acd 100%);

  .user-info {
    width: 58%;

    .ui-contain {
      height: calc(100vh - 112px);
      min-height: 520px;
      overflow: hidden;
      border-radius: 10px;

      .ui-left {
        width: 35%;
        min-width: 280px;
        padding-top: 50px;
        overflow-y: auto;

        img {
          width: 104px;
          height: 104px;
          margin: 0 auto 20px;
        }

        .uil-list {
          padding: 0 11px;
          margin-top: 40px;

          .uil-item {
            height: 58px;
            padding-left: 21px;
            margin-bottom: 8px;
            overflow: hidden;
            font-size: 17px;
            line-height: 58px;
            color: #000;
            cursor: pointer;
            border-radius: 8px;
          }

          .uil-item:hover {
            background: rgb(113 169 254 / 10.2%);
          }

          .uil-active {
            background: rgb(113 169 254 / 10.2%);
          }
        }
      }

      .ui-right {
        width: 65%;
        padding: 31px;
        background-color: #f7f7f7;

        .uir-title {
          margin-bottom: 17px;
          font-size: 15px;
          font-weight: 700;
          color: #1d2130;
        }

        .uir-basic {
          padding: 20px 21px;
          font-size: 15px;
          font-weight: 500;
          color: #4e5968;
          border-radius: 10px;

          .uirb-item {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .uirb-tips {
              font-size: 10px;
              color: #87909c;
            }
          }

          .uirb-text {
            color: #1d2130;
          }

          .forward {
            width: 31px;
            height: 31px;
          }
        }
      }
    }

    .back {
      position: absolute;
      top: 0;
      left: -56px;
      width: 37px;
      height: 37px;
    }
  }
}

.sidebar-logo-container {
  position: absolute;
  top: 0;
  left: 33px;
  display: flex;
  width: 50%;
  height: 48px;
  padding-top: 5px;
  overflow: hidden;
  text-align: center;
  background-color: transparent !important;

  .logo-img {
    width: 35px;
    height: 35px;
    margin-right: 5px;
  }

  .sidebar-title {
    height: 32px;
    margin-top: 2px;
  }
}

.input-pas {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.role-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  .role-item {
    padding: 4px 12px;
    margin: 0 10px 10px;
    font-size: 14px;
    font-weight: 500;
    color: #039fff;
    border: 1px solid #039fff;
    border-radius: 15px;
  }
}

.update-tip {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  color: #039fff;

  p {
    margin-bottom: 8px;
  }
}
</style>
